import React from "react";
import PropTypes from "prop-types";
import { formatCurrency } from "../utils";

export const BankConsolidatedReportTemplate = ({ 
	dataMatrix = [], 
	company = {}, 
	bankAccountsInvolved = [],
	isUSDActive = false,
	exchangeRate = 7.71,
	year = "",
}) => {
	return (
		<html lang="es">
			<head>
				<meta charSet="UTF-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				<title>Reporte Bancario Consolidado - {year}</title>
				<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap" rel="stylesheet"></link>
				<style>
					{`
					@page {
						size: letter landscape; /* Tamaño carta en horizontal */
						margin: 1cm; /* Márgenes más reducidos */
					}
					@media print {
						body {
							zoom: 90%; /* Ajustar escala para que encaje mejor */
						}
						.container {
							width: 100%;
							margin: 0;
							padding: 10px;
						}
						.table {
							font-size: 9px; /* Reducir tamaño de fuente para que entre más contenido */
						}
						.table th, .table td {
							padding: 4px;
						}
					}
					body {
						font-family: 'Inter', sans-serif;
						margin: 0;
						padding: 0;
						background-color: white;
						color: black;
					}
					.container {
						width: 90%;
						margin: auto;
						padding: 20px;
						background-color: #fff;
					}
					.header, .footer, .bank-info, .company-info {
						color: black;
					}
					.bank-details {
						display: flex;
						justify-content: space-between;
						padding-bottom: 20px;
						border-bottom: 2px solid #ddd;
					}
					.company-info h3, .bank-info h3 {
						margin: 0;
						font-size: 18px;
					}
					.company-info p, .bank-info p {
						margin: 4px 0;
						font-size: 14px;
					}
					table {
						width: 100%;
						border-collapse: collapse;
						margin-top: 10px;
						font-size: 10px;
					}
					.table th, .table td {
						padding: 6px;
						text-align: left;
						font-size: 10px;
						white-space: nowrap;
						color: black;
					}
					.table th {
						background-color:rgb(74, 118, 201);
						font-weight: bold; 
						color: white !important;
					}
					.table td {
						white-space: nowrap;
						color: black;
					}
					.footer {
						margin-top: 20px;
						text-align: center;
						font-size: 12px;
					}
					/* Estilos para la jerarquía de clasificación */
					.depth-0 { 
						font-weight: bold;
						background-color: #d1ddeb;
						padding-left: 10px;
					}
					.depth-1 { 
						background-color: #e9f0f7;
						padding-left: 15px;
					}
					.depth-2 { 
						padding-left: 30px; 
					}
					.depth-3 { 
						padding-left: 45px; 
					}
					.depth-4 { 
						padding-left: 60px; 
					}
					/* Aplicar estilos a las filas de saldo */
					tr.saldo-row {												
						background-color:rgb(74, 118, 201);
						font-weight: bold; 
					}
					tr.saldo-row td {
						color: white !important; /* Ahora el texto de saldo será blanco */
					}					
					/* Aplicar estilos a las filas de las cuentas bancarias asignadas */
					tr.bank-row {												
						background-color: #cdcdcd;
						font-weight: bold; 
					}
					tr.bank-row td {
						color: black !important; /* Ahora el texto de saldo será blanco */
					}
					`}
				</style>
			</head>
			<body>
				<div className="container">
					<div className="header">
						<h1>Reporte Bancario Consolidado - {year}</h1>
					</div>

					<div className="bank-details">
						<div className="company-info">
							<h3>{company?.name}</h3>
							<p>NIT: {company?.tid}</p>
						</div>
						<div className="bank-info">
							<h3 style={{ textAlign: "right" }}>Detalles Reporte</h3>
							<p style={{ textAlign: "right" }}>Moneda Seleccionada: {isUSDActive ? "Dolares ($)" : "Quetzales (Q)"}</p>
							<p style={{ textAlign: "right" }}>
								{
									bankAccountsInvolved.reduce((acc, item) => item.currency === (isUSDActive ? "GTQ" : "USD") ? acc + 1 : acc, 0) === 0 ?
										"---" :
										`Tasa de Cambio: Q ${exchangeRate}`
								}
							</p>
						</div>				
					</div>

					<table className="table">
						<thead>
							<tr>
								{dataMatrix[0]?.map((header, index) => (
									<th key={index}>{header}</th>
								))}
							</tr>
						</thead>
						
						<tbody>
							{dataMatrix.slice(1).map((row, rowIndex) => {
								if (!row || !row.length || typeof row[0] !== "string") return null;
								
								const depth = (row[0].match(/^\s*/)?.[0]?.length || 0) / 4;
								const depthClass = `depth-${Math.min(depth, 4)}`;
								
								const isSaldoRow = ["General(Neto):", "Saldo Inicial:", "Saldo Final:"].includes(row[0]?.trim());
								
								return (
									<tr key={rowIndex} className={`${depthClass} ${isSaldoRow ? "saldo-row" : ""}`}>
										{row.map((cell, cellIndex) => {
											let formattedValue = cell;
											if (typeof cell === "number") {
												formattedValue = cell === 0
													? (isSaldoRow ? `${isUSDActive ? "$" : "Q"} ${formatCurrency(cell, isUSDActive ? "$" : "Q")}` : "-")
													: `${isUSDActive ? "$" : "Q"} ${formatCurrency(cell, isUSDActive ? "$" : "Q")}`;
											}
											return (
												<td key={cellIndex} style={{ textAlign: typeof cell === "number" ? "right" : "left" }}>
													{formattedValue}
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</table>

					<div className="footer">
						<h3>Cuentas Bancarias:</h3>
						<ul>
							{bankAccountsInvolved.map(({ id, account_number, bank, currency }) => (
								<li 
									style={{ listStyle: "none" }}
									key={id}
								>
									<strong>Banco:</strong> {bank} | 
									<strong> Cuenta:</strong> {account_number} | 
									<strong> Moneda:</strong> {currency}
								</li>
							))}
						</ul>
					</div>
				</div>
			</body>
		</html>
	);
};

BankConsolidatedReportTemplate.propTypes = {
	dataMatrix: PropTypes.array.isRequired,
	company: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		tid: PropTypes.string.isRequired,
	}).isRequired,
	bankAccountsInvolved: PropTypes.array.isRequired,
	isUSDActive: PropTypes.string.isRequired,
	exchangeRate: PropTypes.number.isRequired,
	year: PropTypes.string.isRequired,
};